import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import Turnstile, { type TurnstileHandle } from "../lib/Turnstile";
import BrandHeader from "../lib/BrandHeader";

/**
 * The half of password reset that sends the email (14.2, Phase 10.5).
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A PAGE IN THE WEB APP AND NOT A SCREEN IN THE EXTENSION.
 *
 * The requirement puts this in the popup: "From the extension, submitting a
 * valid email for reset calls supabase.auth.resetPasswordForEmail(...)". That
 * is no longer possible. `resetPasswordForEmail` posts to `/auth/v1/recover`
 * (read in the installed @supabase/auth-js, not assumed), and that endpoint is
 * captcha-gated by the same global `[auth.captcha]` setting that gates signup
 * and login (7.2). Measured on the running service:
 *
 *     POST /recover, no captcha token       400 captcha_failed
 *     POST /recover, empty captcha token    400 captcha_failed
 *     POST /recover, real token             200 {}
 *
 * An MV3 extension page runs under `script-src 'self'` and cannot load
 * Cloudflare's widget at any price, so the extension cannot obtain a token.
 * This is the identical collision that moved account deletion to
 * `/auth/delete-account` in Phase 10, and it has the same answer: the form
 * lives here and the extension links to it.
 *
 * WHY A ROUTE OF ITS OWN RATHER THAN A MODE ON LoginPage. Not the deletion
 * precedent - that page is separate because its whole purpose is one dangerous
 * action, and requesting a reset email is not dangerous. The reason is the
 * widget. A Turnstile token is SINGLE USE, every page here mounts exactly one
 * widget for its lifetime, and re-rendering a widget re-challenges the user
 * (see Turnstile.tsx). A "forgot" mode on the login page would put two
 * different auth endpoints behind one widget and one spendable token, in the
 * component that already carries this project's sharpest footgun. One page, one
 * widget, one endpoint.
 *
 * It also keeps `/auth/reset` meaning exactly one thing - where the email lands
 * - which is the value `redirectTo` below has to name.
 * ---------------------------------------------------------------------------
 */

/**
 * The one thing this page ever says after a submit.
 *
 * ---------------------------------------------------------------------------
 * ONE MESSAGE FOR EVERY OUTCOME, AND THE 429 IS THE REASON THIS IS NOT
 * DECORATION.
 *
 * The requirement asks for "If an account exists for this email, a password
 * reset link has been sent", and that phrasing is only honest if the page
 * genuinely cannot be made to say otherwise. It was believed to be honest for
 * free, because a FIRST request looks identical either way:
 *
 *     registered address    + token   200 {}
 *     unregistered address  + token   200 {}
 *
 * That is true, and it stops being true on the second request. Measured on the
 * running service (GoTrue v2.191.0), two calls back to back:
 *
 *     registered      200 {}   then  429 over_email_send_rate_limit
 *     unregistered    200 {}   then  200 {}
 *
 * An unregistered address is never sent an email, so it never accumulates the
 * per-address send throttle (`max_frequency` in config.toml) that produces the
 * 429. Two probes inside that window therefore answer "is this address
 * registered" cleanly. THE PAGE CANNOT FIX THAT - it is at the wire level,
 * exactly like `/signup`'s `422 user_already_exists` - so what it can control is
 * what it SHOWS, and it shows this sentence for 200, for 429, and for every
 * other refusal.
 *
 * AND THE OBVIOUS FIX DOES NOT WORK, which is worth knowing before someone
 * tries it. "Keep the throttle but always answer 200" is the right instinct, and
 * it would need a proxy endpoint of our own, since `/auth/v1/*` bypasses this
 * project's Edge Function entirely. Measured before recommending it, with every
 * address probed exactly once so the throttle never fires and both sides return
 * an identical `200 {}` (14 pairs, interleaved):
 *
 *     registered (email IS sent)   mean 140.6 ms   median 136.6 ms   sd 19.7
 *     unknown    (nothing sent)    mean  81.9 ms   median  81.1 ms   sd  7.9
 *     delta +58.8 ms = 3.92 sd, medians not overlapping
 *
 * Rewriting the status code moves the oracle from the response to the clock,
 * and makes it CHEAPER - one probe instead of two. Closing it properly means
 * answering before the send finishes, which also gives up reporting a failed
 * captcha to the user. 10.2.2 measured /users/resolve at 0.26 sd (~119 probes to
 * classify one name); 3.92 sd is a different order of thing.
 *
 * WHY THE ORACLE IS ACCEPTED RATHER THAN CLOSED. Two more measurements:
 *
 *   1. The captcha runs FIRST. Measured: a repeat request for a rate-limited
 *      registered address with no captcha token returns the same
 *      `400 captcha_failed` as one for an address that does not exist. So a
 *      probe costs a solved challenge rather than a bare HTTP request - the
 *      same bound 7.2 accepts for the signup form.
 *   2. `/signup` already answers the same question MORE cheaply. Measured, with
 *      one valid captcha token and a correct TOS acceptance:
 *
 *          registered      422 user_already_exists     (until Phase 12.5)
 *          registered      200, identities []          (since - SignupPage.tsx)
 *          unregistered    200, identities [1]
 *
 *      One request, one challenge, an unambiguous answer - in the response,
 *      though since Phase 12.5 no longer on the screen. `/recover` needs two
 *      requests, two challenges and a timing window to learn the same fact.
 *      Closing the weaker oracle while the stronger one stands one endpoint
 *      over buys nothing, and 10.2.2's work on `/users/resolve` is not weakened
 *      by this: that endpoint is about who holds a USERNAME, which is the
 *      project's identifier. An email address is not.
 *
 * What must not happen is this page ADDING a distinction of its own. Do not
 * look the address up before sending. Do not disable the button for an address
 * believed unregistered. Do not branch this message on `error`. `pnpm e2e`
 * asserts the first-request equality and pins the 429 divergence, so a change
 * in GoTrue's behaviour is reported rather than absorbed.
 * ---------------------------------------------------------------------------
 */
const SENT =
  "If an account exists for this email, a password reset link has been sent. " +
  "Please check your email and follow the instructions.";

export default function ForgotPasswordPage() {
  // Pre-filled when the extension sends someone here from Settings, where the
  // address is already known and retyping it is pointless. It is an ordinary
  // editable field: the page must behave identically whoever is typing, and a
  // read-only box would imply this page knows something about the account.
  const emailFromQuery = useMemo(() => new URLSearchParams(window.location.search).get("email") ?? "", []);

  const [email, setEmail] = useState(emailFromQuery);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstile = useRef<TurnstileHandle | null>(null);

  // Where the emailed link lands. `/auth/reset` is already permitted by
  // `additional_redirect_urls` in config.toml (`http://localhost:3000/**`), so
  // no configuration change was needed for this - verified by following a real
  // link, which 303s to `/auth/reset#access_token=...&type=recovery`.
  //
  // GoTrue silently falls back to `site_url` for a redirect it does not allow
  // rather than refusing the request: measured, `redirect_to=http://evil.example/...`
  // returned 200 and the delivered email pointed at localhost:3000. So a typo
  // here does not fail loudly - it produces a link to the wrong page.
  const redirectTo = useMemo(() => `${window.location.origin}/auth/reset`, []);

  const canSubmit = !!captchaToken && !busy && email.trim().length > 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;
    setBusy(true);

    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
        captchaToken: captchaToken ?? undefined,
      });

      // DELIBERATELY NOT BRANCHED ON `err`. A 429 means the address is
      // registered and was emailed moments ago; a 200 means either an email
      // went out or the address is unknown. Showing them differently is the
      // oracle this page exists to avoid, so `err` is dropped on purpose here
      // rather than ignored by accident - see the comment on SENT.
      void err;
      setSent(true);
    } catch {
      // Only a request that never reached GoTrue lands here - offline, DNS,
      // TLS. A REFUSAL is an `err` above and is not shown. This message says
      // nothing about the address, so retrying with it is safe.
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setBusy(false);

      // The token is spent whether the request succeeded or not, so a second
      // press needs a fresh challenge. Without this the retry fails with
      // `timeout-or-duplicate`, which reads as a rejected address.
      turnstile.current?.reset();
      setCaptchaToken(null);
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen grid place-items-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6 space-y-4">
          <BrandHeader className="" />
          <h1 className="text-xl font-semibold">Check your email</h1>
          <div className="rounded-xl border border-success-line bg-success-bg p-3 text-sm text-success">
            {SENT}
          </div>
          <div className="text-sm text-fg-soft">
            The link opens a page where you choose a new password. After that, log in again
            from the TakeNote extension with your new password.
          </div>
          <div className="text-sm">
            <Link className="text-link hover:underline" to="/auth/login">
              Log in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6">
        <BrandHeader />
        <h1 className="text-xl font-semibold">Reset your password</h1>
        <p className="mt-2 text-sm text-fg-soft">
          Enter the email address for your TakeNote account and we&rsquo;ll send you a link to
          choose a new password.
        </p>

        <form className="mt-6 space-y-3" onSubmit={onSubmit}>
          <div>
            <label className="text-sm font-medium" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="pt-1">
            <Turnstile ref={turnstile} onToken={setCaptchaToken} />
          </div>

          {/* Reachable only if the request threw before GoTrue answered - an
              offline browser, say. A REFUSAL never lands here; see SENT. */}
          {error && (
            <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 w-full rounded-xl bg-primary text-on-primary py-2 disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>

          {/* No standing hint under the button (11.3) - see Turnstile.tsx. */}

          {/* The verbs, as a pair, wherever both are offered (11.3). */}
          <div className="flex items-center justify-between text-sm">
            <Link className="text-link hover:underline" to="/auth/login">
              Log in
            </Link>
            <Link className="text-link hover:underline" to="/auth/signup">
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
